import { FC, ReactNode, Ref, useId } from 'react'

import { Dialog, DialogActions, DialogContent, DialogProps, Theme } from '@mui/material'
import { SystemStyleObject } from '@mui/system'

import { borderRadiusDefault } from '../../../../themes/theme'
import { fullHeightDialogSx } from '../../helpers/dialogPaperProps'
import { DialogLoader } from '../DialogLoader/DialogLoader'
import { DialogRefetchOverlay } from '../DialogRefetchOverlay/DialogRefetchOverlay'
import { UAInsightsDialogTitle } from '../UAInsightsDialogTitle/UAInsightsDialogTitle'
import { UAInsightsErrorState } from '../UAInsightsErrorState/UAInsightsErrorState'

interface UAInsightsDialogProps {
  open: boolean
  onClose: () => void

  /**
   * Title text for the standard `UAInsightsDialogTitle` row. Ignored when
   * `customTitle` is provided.
   */
  title?: ReactNode
  canGoBack?: boolean
  onGoBack?: () => void
  showNetworkSelector?: boolean
  /**
   * Replaces the standard `UAInsightsDialogTitle` row entirely. Use for the
   * compare dialogs that need a bespoke `DialogTitle` layout. Pass `ariaLabel`
   * alongside it — a custom title row bypasses the `aria-labelledby` wiring
   * below, so without one the dialog announces only as "dialog".
   */
  customTitle?: ReactNode
  /** Accessible name for the dialog. Only used with `customTitle`. */
  ariaLabel?: string

  /**
   * `true` once the dialog has data to render. Drives the initial-load gate
   * and (when `false`) the "not found" fallback.
   */
  hasData: boolean
  /** `true` while a fetch is in flight (initial or refetch). */
  isLoading: boolean
  /**
   * `true` when the request failed. Takes precedence over the "not found"
   * fallback while there is no data, since a failed request is a different
   * problem from an empty result and is worth retrying.
   */
  isError?: boolean
  /** Retry action for the error state. Omit to render the error without a button. */
  onRetry?: () => void
  /**
   * Rendered when `!hasData && !isLoading` and `loadingWhenNoData` is `false`.
   * Defaults to rendering nothing.
   */
  notFoundFallback?: ReactNode
  /**
   * When `true`, the loader is shown whenever `!hasData` (regardless of
   * `isLoading`). Matches the original behaviour of dialogs whose loading
   * gate is `!data ?` (e.g. `CreativeAdDetailsDialog`, the compare dialogs).
   */
  loadingWhenNoData?: boolean
  /**
   * When `true`, children are rendered (wrapped in the refetch overlay) even
   * when `!hasData` and the initial load has completed. Use for listing
   * dialogs whose body already handles empty data via `data?.x ?? []` and
   * therefore never want the `notFoundFallback` branch.
   */
  renderChildrenWhenEmpty?: boolean
  /** Skip the dimming `DialogRefetchOverlay` when `hasData && isLoading`. */
  disableRefetchOverlay?: boolean
  /**
   * Optional layout-matched placeholder rendered in place of the default
   * centred `DialogLoader` while the body is in the initial-load state.
   * Lets each dialog substitute a skeleton that matches its real layout
   * (table rows, hero panel, radar + diagnosis, …) so the dialog paper does
   * not visibly resize when data arrives.
   */
  loadingPlaceholder?: ReactNode

  /** Forwarded to MUI `Dialog`. Defaults to `false`. */
  fullScreen?: boolean
  /** Forwarded to MUI `Dialog`. Defaults to `'xl'`. */
  maxWidth?: DialogProps['maxWidth']
  /** Forwarded to MUI `Dialog`. Defaults to `true`. */
  fullWidth?: boolean
  /**
   * `sx` applied to the `Dialog` (controls the paper size). Defaults to
   * `fullHeightDialogSx`. Pass `null` to disable so the paper sizes to
   * content (used by `fullScreen` dialogs).
   */
  paperSx?: DialogProps['sx'] | null

  /** `dividers` on `DialogContent`. Defaults to `true`. */
  dividers?: boolean
  /** Extra `sx` on `DialogContent` (e.g. `{ p: 0 }` for sticky-tab dialogs). */
  contentSx?: DialogProps['sx']
  /** Forwarded ref for the scrollable `DialogContent` element. */
  contentRef?: Ref<HTMLDivElement>

  /** Renders a `DialogActions` row below the content when provided. */
  actions?: ReactNode
  actionsSx?: DialogProps['sx']

  children: ReactNode
}

/**
 * Shared scaffolding for every dialog in the UA Insights feature. Composes
 * the recurring shell (`Dialog` + `UAInsightsDialogTitle` + initial-load
 * gate + in-place refetch overlay + optional actions) so each individual
 * dialog file shrinks to "open flag + data hook + body".
 *
 * Designed to be permissive enough that even the bespoke compare dialogs
 * (`fullScreen`, custom title, no refetch overlay) can use the same wrapper
 * by overriding the relevant props.
 */
export const UAInsightsDialog: FC<UAInsightsDialogProps> = ({
  open,
  onClose,
  title,
  canGoBack,
  onGoBack,
  showNetworkSelector,
  customTitle,
  ariaLabel,
  hasData,
  isLoading,
  isError,
  onRetry,
  notFoundFallback,
  loadingWhenNoData,
  renderChildrenWhenEmpty,
  disableRefetchOverlay,
  loadingPlaceholder,
  fullScreen,
  maxWidth = 'xl',
  fullWidth = true,
  paperSx = fullHeightDialogSx,
  dividers = true,
  contentSx,
  contentRef,
  actions,
  actionsSx,
  children,
}) => {
  // Non-fullscreen dialogs adopt the brand corner radius; fullscreen paper keeps
  // MUI's square edges. Merged after `paperSx` so callers can still override.
  const paperRadiusSx: SystemStyleObject<Theme> = { '& .MuiDialog-paper': { borderRadius: borderRadiusDefault } }
  const titleId = useId()
  const renderBody = (): ReactNode => {
    // A failed request with nothing to fall back on takes precedence over both
    // the loading gate and the "not found" copy. When stale data is still on
    // screen we keep rendering it rather than replacing a usable view with an
    // error — the refetch overlay already signals that something is happening.
    if (isError && !hasData) return <UAInsightsErrorState onRetry={onRetry} disableCard />

    // Initial-load gate. When the caller supplied a layout-matched
    // `loadingPlaceholder`, prefer it over the generic centred spinner so
    // the dialog reads as "rendering" rather than "blocked on a fetch".
    const placeholder = loadingPlaceholder ?? <DialogLoader />
    if (loadingWhenNoData) {
      if (!hasData) return placeholder
    } else if (!hasData) {
      if (isLoading) return placeholder
      if (!renderChildrenWhenEmpty) return notFoundFallback ?? null
    }
    if (disableRefetchOverlay) return children
    return <DialogRefetchOverlay active={isLoading}>{children}</DialogRefetchOverlay>
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth={fullScreen ? undefined : fullWidth}
      maxWidth={fullScreen ? undefined : maxWidth}
      sx={fullScreen ? undefined : [paperRadiusSx, ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : [])]}
      aria-labelledby={customTitle ? undefined : titleId}
      aria-label={customTitle ? ariaLabel : undefined}
    >
      {customTitle ?? (
        <UAInsightsDialogTitle
          title={title}
          titleId={titleId}
          onClose={onClose}
          canGoBack={canGoBack}
          onGoBack={onGoBack}
          showNetworkSelector={showNetworkSelector}
        />
      )}
      <DialogContent ref={contentRef} dividers={dividers} sx={contentSx}>
        {renderBody()}
      </DialogContent>
      {actions !== undefined && (
        // `overflow: hidden` clips any decorative overflow from the actions
        // (e.g. the animated confirmation's expanding ring / sparkles) so it
        // can't add to the dialog's scrollable area and surface a scrollbar.
        <DialogActions sx={[{ overflow: 'hidden' }, ...(actionsSx ? (Array.isArray(actionsSx) ? actionsSx : [actionsSx]) : [])]}>{actions}</DialogActions>
      )}
    </Dialog>
  )
}
